import React, { useMemo } from 'react';
import { Box, makeStyles } from '@material-ui/core';
import cx from 'classnames';

// Strip ANSI escape sequences (colours, cursor moves) that react-lazylog used
// to interpret; rendered raw they would show as control-character noise.
// eslint-disable-next-line no-control-regex
const ANSI_ESCAPE = /[\u001B\u009B][[\]()#;?]*(?:\d{1,4}(?:;\d{0,4})*)?[\dA-PR-TZcf-ntqry=><~]/g;

// A carriage return rewrites the line in a terminal (e.g. tqdm progress bars),
// so only the segment after the last \r is visible.
const cleanLine = (line: string) => (line.split('\r').pop() ?? '').replace(ANSI_ESCAPE, '');

const useStyles = makeStyles(theme => ({
  container: {
    overflow: 'auto',
    width: '100%',
    backgroundColor: theme.palette.grey[50],
    borderRadius: 6,
    padding: theme.spacing(1),
    fontFamily: 'Menlo, Monaco, Consolas, "Courier New", monospace',
    fontSize: 12,
    lineHeight: 1.5,
  },
  row: {
    display: 'flex',
    minHeight: '1.5em',
  },
  gutter: {
    flex: '0 0 auto',
    width: 48,
    paddingRight: theme.spacing(1.5),
    textAlign: 'right',
    color: theme.palette.text.disabled,
    userSelect: 'none',
  },
  content: {
    flex: '1 1 auto',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
}));

export interface TrainingLogViewerProps {
  text: string;
  height?: number;
  /** Applied to every log line — kept for existing e2e selectors. */
  lineClassName?: string;
}

/**
 * Renders training-job logs in a plain scrollable container.
 *
 * Replaces `react-lazylog`, whose `react-virtualized` list dropped its trailing
 * rows and left the panel blank near the end of the scroll (LandingLens
 * deploy-page "Logs" bug). Training logs are small, so the virtualization that
 * caused the bug buys nothing here. Line numbers and ANSI stripping match what
 * react-lazylog rendered by default.
 */
const TrainingLogViewer: React.FC<TrainingLogViewerProps> = ({
  text,
  height = 600,
  lineClassName,
}) => {
  const styles = useStyles();
  const lines = useMemo(() => text.split('\n').map(cleanLine), [text]);
  return (
    <Box className={styles.container} style={{ height }}>
      {lines.map((line, index) => (
        <div key={index} className={cx(styles.row, lineClassName)}>
          <span className={styles.gutter}>{index + 1}</span>
          <span className={styles.content}>{line}</span>
        </div>
      ))}
    </Box>
  );
};

export default TrainingLogViewer;
